
import Vue, { toRaw } from "vue"
import { mapGetters } from "vuex"
import type { SwiperOptions } from "swiper"
import breakpoints from "@/assets/style/resources/breakpoints/module.scss"
import { scrollToTop } from "@/utils/_"
import type { Day, Track, Session, FullSession } from "@/types/api/program"

export default Vue.extend({
  layout: "index",

  // validate ({ store }) { return store.getters["user/isAdmin"] },

  async asyncData ({ error, route, $api, redirect, app }) {
    const fail = () => error({ statusCode: 404, message: "Not Found" })

    const programApi = $api.program
    const { getList } = $api.helpers

    let [res, err] = await programApi.getDays()
    if (err) return console.error(err)
    const days: Day[] = getList(res)
    if (!days.length) return fail()

    const query = { ...route.query }
    const { day, track } = query
    const isDayWrong = !day || !days.find(d => d.alias === day)
    if (isDayWrong) {
      query.day = days[0].alias
      return redirect(app.localePath("/video"), query)
    }

    [res, err] = await programApi.getGroups()
    if (err) return console.error(err)
    const groups = getList(res)
    if (!groups.length) return fail()
    const mainGroup = groups[0].alias;

    [res, err] = await programApi.getTracks(({
      params: { "filter[day]": query.day, "filter[group]": mainGroup },
    }))
    if (err) return console.error(err)
    const tracks: Track[] = getList(res).filter((t: Track) =>
      t.liveData.embedCode)
    if (!tracks.length) return fail()

    const isTrackWrong = !track || !tracks.find(t => t.hall.alias === track)
    if (isTrackWrong) {
      query.track = tracks[0].hall.alias
      return redirect(app.localePath("/video"), query)
    }

    return { days, mainGroup, tracks }
  },

  data: () => ({
    mode: "session" as "track" | "session",
    days: [] as Day[],
    mainGroup: "",
    tracks: [] as Track[],
    fullTrack: null as Track | null,
    sessions: [] as Session[],
    currentSession: null as FullSession | null,
    swiperOptions: toRaw({
      slidesPerView: 1,
      spaceBetween: 20,
      // allowTouchMove: false,
      // grabCursor: false,
      breakpoints: {
        [breakpoints.md]: { slidesPerView: 2, spaceBetween: 30 },
        [breakpoints.lg]: { slidesPerView: 3, spaceBetween: 30 },
        [breakpoints.xl]: { slidesPerView: 4, spaceBetween: 40 },
      },
    } as SwiperOptions),
  }),

  async fetch () {
    await this.fetchSessions()
    await this.fetchCurrentSession()
    await this.fetchFullTrack()
  },

  head () {
    return { title: this.currentSession?.title }
  },

  computed: {
    ...mapGetters("user", ["isAdmin"]),

    day: {
      get (): string { return this.$route.query.day as string || "" },
      set (v: string) { this.setParam("day", v) },
    },
    track: {
      get (): string { return this.$route.query.track as string || "" },
      set (v: string) { this.setParam("track", v) },
    },
  },

  watch: {
    async day () { await this.reset() },
    async track () { await this.reset() },

    async "$route.query.session" () { await this.fetchCurrentSession() },

    async currentSession (currentSession) {
      await this.$nextTick()
      if (currentSession) scrollToTop(false)
    },
  },

  watchQuery (newQuery, oldQuery) {
    return newQuery.day !== oldQuery.day ||
      newQuery.track !== oldQuery.track
  },

  methods: {
    setParam (k: string, v: string | undefined) {
      this.$router.push({
        query: { ...this.$route.query, [k]: v || undefined },
      })
    },

    async reset () {
      await this.fetchSessions()
      this.setParam("session", undefined)
      await this.fetchCurrentSession()
      await this.fetchFullTrack()
    },

    async fetchSessions () {
      const [res, err] = await this.$api.program.getSessions({
        params: {
          "filter[day]": this.day,
          "filter[group]": this.mainGroup,
          "filter[hall]": this.track,
        },
      })
      if (err) return console.error(err)
      this.sessions = this.$api.helpers.getList(res)
    },

    async fetchCurrentSession () {
      this.currentSession = null // Хак чтобы сбросить состояние плеера
      const { sessions } = this
      const sessionAlias = this.$route.query.session
      const session =
        sessions.find(s => s.alias === sessionAlias) || sessions[0]
      const [res, err] = await this.$api.program.getSession(session.alias)
      if (err) console.error(err)
      else this.currentSession = res
    },

    async fetchFullTrack () {
      this.fullTrack = null
      await this.$nextTick()
      this.fullTrack = this.tracks.find(t => t.hall?.alias === this.track) || null
    },
  },
})
